import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import {
  Sun, Moon, Sparkles, ArrowRight, Users, Mail,
  Phone, TrendingUp, AlertTriangle, Target, Zap,
  RefreshCw, ExternalLink
} from 'lucide-react';
import api from '../../lib/api';
import { useNavigate } from 'react-router-dom';

const INSIGHT_ICONS = {
  hot_lead: Target,
  pipeline: TrendingUp,
  emails: Mail,
  cadence: Zap,
  calls: Phone,
  alert: AlertTriangle,
};

const INSIGHT_COLORS = {
  high: 'border-l-orange-500 bg-orange-50/50 dark:bg-orange-950/20',
  medium: 'border-l-blue-500 bg-blue-50/50 dark:bg-blue-950/20',
  low: 'border-l-slate-300 bg-slate-50/50 dark:bg-slate-900/20',
};

function getTimeGreetingIcon() {
  const hour = new Date().getHours();
  if (hour < 12) return Sun;
  if (hour < 18) return Sun;
  return Moon;
}

export default function BrainMorningBriefing() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchBriefing = useCallback(async (forceRefresh = false) => {
    try {
      const url = '/api/brain/morning-briefing' + (forceRefresh ? '?refresh=1' : '');
      const res = await api.get(url);
      setData(res.data);
    } catch { /* silent */ }
    finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchBriefing(); }, [fetchBriefing]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchBriefing(true);
  };

  if (loading) {
    return (
      <Card data-testid="brain-morning-briefing">
        <CardContent className="py-5 space-y-3">
          <Skeleton className="h-5 w-48" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-16 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const GreetIcon = getTimeGreetingIcon();
  const insights = data.insights || [];
  const stats = data.stats || {};

  return (
    <Card
      className="relative overflow-hidden border-0 shadow-md"
      data-testid="brain-morning-briefing"
    >
      {/* Warm gradient accent */}
      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-amber-400 via-orange-400 to-rose-400" />

      <CardContent className="pt-5 pb-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/20">
              <GreetIcon className="h-4.5 w-4.5 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-900 dark:text-white leading-tight" data-testid="briefing-greeting">
                {data.greeting}
              </h3>
            </div>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            data-testid="briefing-refresh-btn"
            title="Refresh briefing"
          >
            <RefreshCw className={`h-3.5 w-3.5 text-slate-400 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Headline */}
        {data.headline && (
          <div className="mb-3 animate-[fadeUp_0.3s_ease-out]">
            <p className="text-base font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-amber-500 shrink-0" />
              {data.headline}
            </p>
          </div>
        )}

        {/* Insights */}
        {insights.length > 0 && (
          <div className="space-y-2 mb-3">
            {insights.map((insight, i) => {
              const Icon = INSIGHT_ICONS[insight.type] || Sparkles;
              const colorClass = INSIGHT_COLORS[insight.priority] || INSIGHT_COLORS.medium;
              return (
                <div
                  key={i}
                  className={`flex items-start gap-2.5 p-2.5 rounded-lg border-l-2 ${colorClass} animate-[fadeUp_0.3s_ease-out]`}
                  style={{ animationDelay: `${i * 100}ms`, animationFillMode: 'both' }}
                  data-testid={`briefing-insight-${i}`}
                >
                  <Icon className="h-4 w-4 text-slate-500 dark:text-slate-400 shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-700 dark:text-slate-300 leading-snug">
                      {insight.text}
                    </p>
                    {insight.action_label && insight.action_url && (
                      <Button
                        variant="link"
                        size="sm"
                        className="h-auto p-0 mt-1 text-xs text-blue-600 dark:text-blue-400 font-medium"
                        onClick={() => navigate(insight.action_url)}
                        data-testid={`briefing-action-${i}`}
                      >
                        {insight.action_label}
                        <ArrowRight className="h-3 w-3 ml-1" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Stats summary */}
        {data.stats_summary && (
          <p className="text-xs text-slate-400 dark:text-slate-500 italic" data-testid="briefing-stats-summary">
            {data.stats_summary}
          </p>
        )}

        {/* Quick stats row */}
        <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800">
          <div className="flex items-center gap-4 text-xs text-slate-500">
            {stats.hot > 0 && (
              <span className="flex items-center gap-1" data-testid="briefing-stat-hot">
                <Target className="h-3 w-3 text-red-500" />
                <span className="font-medium text-red-600">{stats.hot}</span> hot
              </span>
            )}
            {stats.emails_pending > 0 && (
              <span className="flex items-center gap-1" data-testid="briefing-stat-emails">
                <Mail className="h-3 w-3 text-blue-500" />
                <span className="font-medium text-blue-600">{stats.emails_pending}</span> to send
              </span>
            )}
            {stats.cadences > 0 && (
              <span className="flex items-center gap-1" data-testid="briefing-stat-cadences">
                <Zap className="h-3 w-3 text-purple-500" />
                <span className="font-medium text-purple-600">{stats.cadences}</span> cadences
              </span>
            )}
            {stats.pipeline_value > 0 && (
              <span className="flex items-center gap-1" data-testid="briefing-stat-pipeline">
                <TrendingUp className="h-3 w-3 text-emerald-500" />
                ${(stats.pipeline_value / 1000).toFixed(0)}k pipeline
              </span>
            )}
          </div>
        </div>
      </CardContent>

      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </Card>
  );
}
